// iter683 — Visual preview of what a thermal receipt will look like
// when sent to the physical printer. Uses the EXACT same line-builder
// (`buildReceiptLines` from eposPrint.js) as the real print path, so
// the preview is bit-for-bit identical to the actual paper output —
// same column count, same wrapping, same divider widths, same size=2
// rules for the TOTAL.
//
// Two flavors:
//   • <ReceiptPreview content paperMm /> — the bare paper slip.
//   • <ReceiptPreviewDialog open onOpenChange initialContent
//                            paperMm /> — opens a Dialog with sample
//                                          content + paper-width
//                                          toggle. Used from the
//                                          Printers settings page.
//
// Why HTML instead of <canvas>? Thermal printers use a monospace
// bitmap font and we already produce the line list as padded text,
// so a fixed-width div renders pixel-equivalent output, supports
// Arabic and other scripts natively, and is trivial to copy/screenshot.

import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { buildReceiptLines, buildZatcaQrPayload } from '@/lib/eposPrint';
import { resolveReceiptLogoUrl } from '@/lib/receiptLogo';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

/**
 * iter683 — Default sample payload used by ReceiptPreviewDialog when
 * the operator hasn't run a real order yet. Lets them tweak the
 * branch/header/footer/logo fields without needing live POS data.
 */
const DEFAULT_SAMPLE = {
  company: 'SmartOERP',
  branch: 'Main Branch',
  title: 'Simplified Tax Invoice',
  vat: '300000000003',
  zatca_seller_name: 'SmartOERP — شركة معيار الذكي',
  header: 'MTR - شركة معيار الذكي\nفاتورة ضريبية مبسطة',
  order_number: '77',
  check_number: '100322',
  order_type: 'Pick Up',
  date: new Date().toISOString(),
  opened_at: new Date(Date.now() - 30000).toISOString(),
  server: 'Cashier',
  items: [
    { qty: 1, name: 'Catering Services', subtotal: 12.00 },
    { qty: 1, name: 'Water', subtotal: 0.00 },
    { qty: 1, name: 'Water .3ml', subtotal: 1.00 },
  ],
  subtotal: 11.30,
  discount: 0,
  tax: 1.70,
  tax_pct: 15,
  total: 13.00,
  currency: '﷼',
  payment_method: 'cash',
  payment_label: 'Bilad',
  footer: '',
  // iter684 — all toggles default ON so the preview demonstrates every
  // section. Operators can switch any off in Settings → Receipt.
  options: {
    show_logo: true, show_arabic_header: true, show_order_box: true,
    show_order_type: true, show_printed_at: true, show_column_headers: true,
    show_order_number: true, show_check_number: true, show_subtotal: true,
    show_vat: true, show_payment_method: true, show_products_count: true,
    show_zatca_qr: true, show_company_at_bottom: true, hide_free_modifiers: true,
  },
};

/**
 * Bare preview component. Renders the receipt as a fixed-width HTML
 * column that mimics the thermal printer's monospace output.
 */
export function ReceiptPreview({ content, paperMm = 80, logoUrl = '' }) {
  const { lines, cols } = buildReceiptLines(content, paperMm);
  const opt = content?.options || {};
  // iter686 — Default OFF: tenant must opt-in via Settings → Receipt
  // Settings before the QR appears on slips, matching the print path.
  const showQr = ('show_zatca_qr' in opt) ? !!opt.show_zatca_qr : false;
  const zatcaPayload = showQr ? buildZatcaQrPayload(content) : '';

  // Approx physical width:
  //   • 58 mm paper @ 32 cols → ~8 px / char
  //   • 80 mm paper @ 42 cols → ~9 px / char
  // We render at 2x for crisp rendering on retina screens.
  const charPx = paperMm <= 58 ? 8 : 9;
  const widthPx = cols * charPx + 16; // 8px padding each side
  const sizeOneLineHeight = charPx * 1.55;

  return (
    <div
      className="bg-white rounded-md shadow-md border border-zinc-200 mx-auto"
      style={{ width: widthPx, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace', fontSize: charPx + 'px', color: '#111', padding: '14px 8px 18px' }}
      data-testid="receipt-preview-slip"
    >
      {logoUrl && (
        <div className="flex justify-center mb-2" data-testid="receipt-preview-logo">
          <img
            src={logoUrl}
            alt="logo preview"
            style={{ maxWidth: cols * charPx * 0.7, maxHeight: 80, filter: 'grayscale(100%) contrast(1.6)', imageRendering: 'pixelated' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      )}
      {lines.map((ln, i) => {
        const isSize2 = ln.size === 2;
        // iter686 — Switched divider from Unicode '─' to ASCII '-' for
        // printer code-page safety; preview regex updated to match.
        const isDivider = /^-+$/.test(ln.text || '');
        return (
          <div
            key={i}
            style={{
              whiteSpace: 'pre',
              fontWeight: ln.bold ? 700 : 400,
              transform: isSize2 ? 'scaleX(2)' : 'none',
              transformOrigin: 'left center',
              marginTop: isSize2 ? sizeOneLineHeight * 0.4 : 0,
              marginBottom: isSize2 ? sizeOneLineHeight * 0.4 : 0,
              lineHeight: isSize2 ? '1.55em' : '1.4em',
              letterSpacing: isDivider ? '-1px' : '0',
              color: isDivider ? '#666' : '#111',
            }}
          >
            {ln.text || '\u00A0'}
          </div>
        );
      })}
      {/* iter684 — ZATCA QR rendered after the line block. Matches the
          placement of the <symbol> element in the ePOS XML. */}
      {zatcaPayload && (
        <div className="flex justify-center my-2" data-testid="receipt-preview-qr">
          <QRCodeSVG
            value={zatcaPayload}
            size={Math.min(cols * charPx * 0.6, 140)}
            level="L"
            includeMargin={false}
          />
        </div>
      )}
      <div style={{ borderTop: '1px dashed #bbb', marginTop: 12, paddingTop: 6, fontSize: 9, color: '#999', textAlign: 'center' }}>
        ✂ Cut here · {paperMm} mm · {cols} cols
      </div>
    </div>
  );
}

/**
 * Full preview dialog with editable sample data. Lets the operator
 * tweak header / footer / logo and instantly see the result, so they
 * never waste a paper roll testing on hardware.
 */
export function ReceiptPreviewDialog({ open, onOpenChange, initialContent, branding, defaultPaperMm = 80 }) {
  const [paperMm, setPaperMm] = useState(defaultPaperMm);
  const [override, setOverride] = useState({});

  const content = {
    ...DEFAULT_SAMPLE,
    ...(initialContent || {}),
    ...override,
  };

  const logoUrl = (() => {
    if (override.logo_url !== undefined) return override.logo_url;
    if (initialContent?.logo_url) return initialContent.logo_url;
    // Shared resolver: Branch Logo → Global Logo.
    return resolveReceiptLogoUrl(branding);
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92dvh] overflow-y-auto" data-testid="receipt-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>🧾</span>
            Receipt preview
            <span className="ml-2 text-[10px] font-normal text-muted-foreground">(matches the actual thermal print, character for character)</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 pt-2">
          {/* Editable sample data */}
          <div className="space-y-3 max-w-md">
            <div className="flex items-center gap-2 text-xs">
              <Label className="text-[11px]">Paper width</Label>
              <div className="flex items-center gap-1">
                {[58, 80].map((mm) => (
                  <button
                    key={mm}
                    type="button"
                    onClick={() => setPaperMm(mm)}
                    className={`px-2 py-0.5 rounded text-[11px] border ${paperMm === mm ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-zinc-700 border-zinc-300'}`}
                    data-testid={`receipt-preview-paper-${mm}`}
                  >
                    {mm} mm
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[11px]">Branch name</Label>
              <Input
                value={content.branch || ''}
                onChange={(e) => setOverride({ ...override, branch: e.target.value })}
                className="h-8 text-xs mt-1"
                data-testid="receipt-preview-branch"
              />
            </div>
            <div>
              <Label className="text-[11px]">Custom header (Settings → Branch → receipt header)</Label>
              <Textarea
                value={content.header || ''}
                onChange={(e) => setOverride({ ...override, header: e.target.value })}
                className="text-xs mt-1 min-h-[60px]"
                placeholder="e.g. Tax Invoice / فاتورة ضريبية"
                data-testid="receipt-preview-header"
              />
            </div>
            <div>
              <Label className="text-[11px]">Footer (Settings → Branch → receipt footer)</Label>
              <Textarea
                value={content.footer || ''}
                onChange={(e) => setOverride({ ...override, footer: e.target.value })}
                className="text-xs mt-1 min-h-[60px]"
                placeholder="e.g. Thank you for visiting! Visit again at smartoerp.com"
                data-testid="receipt-preview-footer"
              />
            </div>
            <div>
              <Label className="text-[11px]">Company (shown at bottom)</Label>
              <Input
                value={content.company || ''}
                onChange={(e) => setOverride({ ...override, company: e.target.value })}
                className="h-8 text-xs mt-1"
                data-testid="receipt-preview-company"
              />
            </div>
            <div>
              <Label className="text-[11px]">Logo URL (test rendering)</Label>
              <Input
                value={logoUrl}
                onChange={(e) => setOverride({ ...override, logo_url: e.target.value })}
                className="h-8 text-xs mt-1 font-mono"
                placeholder="https://… (will be downscaled & dithered to B/W on the printer)"
                data-testid="receipt-preview-logo-url"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                If the logo doesn&apos;t appear here, it won&apos;t print either. Common cause: cross-origin URL without CORS headers.
              </p>
            </div>

            <div className="pt-2 border-t border-zinc-200">
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                💡 The preview uses the same line-builder as the printer driver, so the exact wrapping, padding and centering you see here is what the paper will show. The TOTAL line is rendered double-width to match the printer&apos;s <code>size=2</code> directive.
              </p>
            </div>
          </div>

          {/* Live preview */}
          <div className="bg-zinc-100 rounded-lg p-4 flex items-start justify-center min-w-0">
            <ReceiptPreview content={content} paperMm={paperMm} logoUrl={logoUrl} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="receipt-preview-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
